import { useEffect, useState, useMemo } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  opacity: number;
  drift: number;
}

interface GiftParticlesProps {
  accentColor: string;
  isDark?: boolean;
  mode?: "burst" | "ambient";
}

const GiftParticles = ({ accentColor, isDark = false, mode = "burst" }: GiftParticlesProps) => {
  const particles = useMemo(() => {
    if (mode === "ambient") {
      return Array.from({ length: 6 }, (_, i) => ({
        id: i,
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 80,
        size: 2 + Math.random() * 3,
        delay: Math.random() * 4000,
        duration: 5000 + Math.random() * 5000,
        opacity: 0.15 + Math.random() * 0.2,
        drift: -20 - Math.random() * 40,
      }));
    }
    // burst
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 50 + (Math.random() - 0.5) * 50,
      y: 50 + (Math.random() - 0.5) * 40,
      size: 2 + Math.random() * 5,
      delay: Math.random() * 250,
      duration: 700 + Math.random() * 500,
      opacity: 0.6 + Math.random() * 0.3,
      drift: -40 - Math.random() * 60,
    }));
  }, [mode]);

  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (mode === "burst") {
      const t = setTimeout(() => setVisible(false), 1400);
      return () => clearTimeout(t);
    }
  }, [mode]);

  if (!visible) return null;

  const color = isDark ? "rgba(212, 175, 55," : accentColor.startsWith("rgba") ? accentColor.replace(/[\d.]+\)$/, "") : accentColor;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: isDark ? `rgba(212, 175, 55, ${p.opacity})` : accentColor,
            opacity: 0,
            animation: mode === "ambient"
              ? `gift-particle-ambient ${p.duration}ms ease-in-out ${p.delay}ms infinite`
              : `gift-particle-burst ${p.duration}ms ease-out ${p.delay}ms forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes gift-particle-burst {
          0% { opacity: ${0.7}; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) translateY(-70px) scale(0.2); }
        }
        @keyframes gift-particle-ambient {
          0%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          50% { opacity: 0.3; transform: translate(-50%, calc(-50% - 20px)) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default GiftParticles;
